import { useState, useEffect, useCallback } from 'react';
import { getHealth, getPins, getSites, getStores, getGlobalStats, getSubmissions, getDashboardSummary, getRecentActivity, getSystemHealth, getStorageUsage, getRequestStats } from '../api/client';

function formatTimeAgo(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));

  if (diffMinutes < 1) return 'Just now';
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays}d ago`;
}

interface HealthData {
  status: string;
  timestamp: string;
  services: Record<string, string>;
}

interface StatsData {
  totalPins: number;
  totalSites: number;
  totalStores: number;
  totalUsers: number;
  eventsToday: number;
  eventsThisWeek: number;
  pendingPins: number;
  approvedPins: number;
  aiApprovedPins: number;
  pendingSubmissions: number;
  lastSyncTime: string;
}

interface RecentSubmission {
  id: string;
  name?: string;
  sku?: string;
  created_at: string;
}

interface ActivityItem {
  id: string;
  type: string;
  title: string;
  meta: string;
  icon: string;
  iconBg: string;
  createdAt: string;
}

interface ServiceHealth {
  status: string;
  latency?: string;
  value?: string;
}

interface StorageItem {
  used: string;
  total: string;
  percent: number;
}

export default function Dashboard() {
  const [_health, setHealth] = useState<HealthData | null>(null);
  const [stats, setStats] = useState<StatsData | null>(null);
  const [recentSubmissions, setRecentSubmissions] = useState<RecentSubmission[]>([]);
  const [recentActivity, setRecentActivity] = useState<ActivityItem[]>([]);
  const [systemHealth, setSystemHealth] = useState<Record<string, ServiceHealth>>({});
  const [storage, setStorage] = useState<Record<string, StorageItem>>({});
  const [chartData, setChartData] = useState<{ days: string[]; percentages: number[] }>({ days: ['M', 'T', 'W', 'T', 'F', 'S', 'S'], percentages: [0, 0, 0, 0, 0, 0, 0] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchData = useCallback(async () => {
    try {
      // Try to use the admin summary endpoint first
      let summaryData = null;
      try {
        const summaryResult = await getDashboardSummary();
        summaryData = summaryResult.data;
      } catch {
        // Fall back to individual API calls if summary endpoint not available
      }

      const [healthData, sitesData, storesData, globalStats, submissionsData] = await Promise.all([
        getHealth(),
        getSites(),
        getStores(),
        getGlobalStats().catch(() => ({ data: { totalUsers: 0, eventsToday: 0, eventsThisWeek: 0 } })),
        getSubmissions(1, 5, 'pending').catch(() => ({ data: { submissions: [], pagination: { total: 0 } } })),
      ]);

      // Fetch pin counts by status
      const [allPins, pendingPins, approvedPins, aiApprovedPins] = await Promise.all([
        getPins(1, 1, 'all').catch(() => ({ success: false, data: { pins: [], pagination: { total: 0, page: 1, limit: 1, pages: 0 } } })),
        getPins(1, 1, 'pending').catch(() => ({ success: false, data: { pins: [], pagination: { total: 0, page: 1, limit: 1, pages: 0 } } })),
        getPins(1, 1, 'approved').catch(() => ({ success: false, data: { pins: [], pagination: { total: 0, page: 1, limit: 1, pages: 0 } } })),
        getPins(1, 1, 'ai_approved').catch(() => ({ success: false, data: { pins: [], pagination: { total: 0, page: 1, limit: 1, pages: 0 } } })),
      ]);

      // Fetch additional dashboard data
      const [activityResult, healthResult, storageResult, requestStatsResult] = await Promise.all([
        getRecentActivity(4).catch(() => ({ data: { activity: [] } })),
        getSystemHealth().catch(() => ({ data: { services: {} } })),
        getStorageUsage().catch(() => ({ data: { storage: {} } })),
        getRequestStats().catch(() => ({ data: { days: ['M', 'T', 'W', 'T', 'F', 'S', 'S'], percentages: [0, 0, 0, 0, 0, 0, 0] } })),
      ]);

      setHealth(healthData);
      setStats({
        totalPins: summaryData?.pins?.total ?? allPins.data?.pagination?.total ?? 0,
        totalSites: sitesData.data?.sites?.length || 0,
        totalStores: storesData.data?.stores?.length || 0,
        totalUsers: summaryData?.users?.total ?? globalStats.data?.totalUsers ?? 0,
        eventsToday: summaryData?.events?.today ?? globalStats.data?.eventsToday ?? 0,
        eventsThisWeek: summaryData?.events?.thisWeek ?? globalStats.data?.eventsThisWeek ?? 0,
        pendingPins: summaryData?.pins?.pending ?? pendingPins.data?.pagination?.total ?? 0,
        approvedPins: summaryData?.pins?.approved ?? approvedPins.data?.pagination?.total ?? 0,
        aiApprovedPins: summaryData?.pins?.aiApproved ?? aiApprovedPins.data?.pagination?.total ?? 0,
        pendingSubmissions: summaryData?.submissions?.pending ?? submissionsData.data?.pagination?.total ?? 0,
        lastSyncTime: new Date().toLocaleTimeString(),
      });

      setRecentSubmissions((submissionsData.data?.submissions as RecentSubmission[]) || []);
      setRecentActivity(activityResult.data?.activity || []);
      setSystemHealth(healthResult.data?.services || {});
      setStorage(storageResult.data?.storage || {});
      setChartData({
        days: requestStatsResult.data?.days || ['M', 'T', 'W', 'T', 'F', 'S', 'S'],
        percentages: requestStatsResult.data?.percentages || [0, 0, 0, 0, 0, 0, 0],
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8 text-gray-500">
        Loading dashboard...
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 text-red-500 p-4 rounded-lg">
        {error}
      </div>
    );
  }

  // Transform system health data for display
  const healthItems = [
    { label: 'D1 Database', key: 'database', color: 'latency' },
    { label: 'R2 Storage', key: 'r2Storage', color: 'value' },
    { label: 'Workers', key: 'workers', color: 'value' },
    { label: 'Queues', key: 'queues', color: 'value' },
    { label: 'KV Cache', key: 'kvCache', color: 'value' },
    { label: 'OpenAI', key: 'openai', color: 'value' },
  ].map(item => {
    const service = systemHealth[item.key];
    return {
      label: item.label,
      value: service?.latency || service?.value || 'OK',
      status: service?.status || 'healthy',
    };
  });

  // Transform storage data for display
  const storageItems = [
    { label: 'R2 Images', key: 'r2Images', color: 'bg-blue-500' },
    { label: 'D1 Database', key: 'd1Database', color: 'bg-green-500' },
    { label: 'KV Storage', key: 'kvStorage', color: 'bg-purple-500' },
  ].map(item => {
    const storageData = storage[item.key];
    return {
      label: item.label,
      used: storageData?.used || '0 MB',
      total: storageData?.total || '0 GB',
      percent: storageData?.percent || 0,
      color: item.color,
    };
  });

  return (
    <div>
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start gap-4 mb-6 sm:mb-8 pb-5 border-b-2 border-gray-200">
        <div>
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-800 mb-1">Dashboard</h2>
          <div className="text-gray-500 text-sm sm:text-base">System overview and quick actions</div>
        </div>
        <div className="flex gap-2 w-full sm:w-auto">
          <button className="flex-1 sm:flex-none px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors text-sm">
            Export Data
          </button>
          <button className="flex-1 sm:flex-none px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors text-sm">
            Sync Now
          </button>
        </div>
      </div>

      {/* Status Bar */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4 bg-white p-3 px-4 sm:px-5 rounded-lg shadow mb-6">
        <div className="flex items-center gap-3">
          <div className="w-3 h-3 rounded-full bg-green-500 animate-pulse" />
          <div className="font-medium text-slate-800">All Systems Operational</div>
        </div>
        <div className="text-gray-500 text-sm hidden sm:block">Last sync: {stats?.lastSyncTime || 'Unknown'}</div>
        <button onClick={fetchData} className="text-blue-500 text-sm hover:underline ml-2">Refresh</button>
        <div className="flex flex-wrap gap-3 sm:gap-4 sm:ml-auto">
          <a
            href="https://dash.cloudflare.com/?to=/:account/workers/services/view/pincove-api/production/observability/logs-live"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-500 text-sm flex items-center gap-1 hover:underline no-underline"
          >
            <span>&#x1F4CB;</span> Live Logs
          </a>
          <a
            href="https://dash.cloudflare.com/?to=/:account/workers/services/view/pincove-api/production/observability/metrics"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-500 text-sm flex items-center gap-1 hover:underline no-underline"
          >
            <span>&#x1F4CA;</span> Metrics
          </a>
          <a
            href="https://dash.cloudflare.com/?to=/:account/d1/databases"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-500 text-sm flex items-center gap-1 hover:underline no-underline"
          >
            <span>&#x1F5C4;</span> D1
          </a>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-5 mb-6 sm:mb-8">
        <div className="bg-white p-4 sm:p-5 rounded-lg shadow">
          <div className="flex justify-between items-start mb-2">
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-green-100 flex items-center justify-center text-lg sm:text-xl">
              &#x1F4CC;
            </div>
          </div>
          <div className="text-2xl sm:text-3xl font-bold text-slate-800 mb-0.5">
            {stats?.totalPins?.toLocaleString() || 0}
          </div>
          <div className="text-gray-500 text-sm">Total Pins</div>
          <div className="mt-2 pt-2 border-t border-gray-200 text-xs text-gray-500 hidden sm:block">
            {stats?.approvedPins} approved | {stats?.pendingPins} pending
          </div>
        </div>

        <div className="bg-white p-4 sm:p-5 rounded-lg shadow">
          <div className="flex justify-between items-start mb-2">
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-blue-100 flex items-center justify-center text-lg sm:text-xl">
              &#x1F465;
            </div>
          </div>
          <div className="text-2xl sm:text-3xl font-bold text-slate-800 mb-0.5">
            {stats?.totalUsers?.toLocaleString() || 0}
          </div>
          <div className="text-gray-500 text-sm">Total Users</div>
          <div className="mt-2 pt-2 border-t border-gray-200 text-xs text-gray-500 hidden sm:block">
            Active today: {stats?.eventsToday?.toLocaleString() || 0}
          </div>
        </div>

        <div className="bg-white p-4 sm:p-5 rounded-lg shadow">
          <div className="flex justify-between items-start mb-2">
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-orange-100 flex items-center justify-center text-lg sm:text-xl">
              &#x1F3EA;
            </div>
          </div>
          <div className="text-2xl sm:text-3xl font-bold text-slate-800 mb-0.5">
            {stats?.totalStores?.toLocaleString() || 0}
          </div>
          <div className="text-gray-500 text-sm">Stores</div>
          <div className="mt-2 pt-2 border-t border-gray-200 text-xs text-gray-500 hidden sm:block">
            Across {stats?.totalSites} sites
          </div>
        </div>

        <div className="bg-white p-4 sm:p-5 rounded-lg shadow">
          <div className="flex justify-between items-start mb-2">
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-pink-100 flex items-center justify-center text-lg sm:text-xl">
              &#x1F4CA;
            </div>
          </div>
          <div className="text-2xl sm:text-3xl font-bold text-slate-800 mb-0.5">
            {stats?.eventsToday?.toLocaleString() || 0}
          </div>
          <div className="text-gray-500 text-sm">Events Today</div>
          <div className="mt-2 pt-2 border-t border-gray-200 text-xs text-gray-500 hidden sm:block">
            Last 24 hours
          </div>
        </div>
      </div>

      {/* Dashboard Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
        {/* Left Column - 2/3 width on desktop */}
        <div className="lg:col-span-2 flex flex-col gap-4 sm:gap-6">
          {/* Moderation Queue */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="bg-slate-700 text-white px-4 sm:px-5 py-3 sm:py-4 flex justify-between items-center">
              <span className="font-bold text-sm sm:text-base">&#x1F4E5; Pending Submissions</span>
              <a href="/submissions" className="text-gray-300 text-xs sm:text-sm hover:text-white no-underline">
                View All ({stats?.pendingSubmissions || 0})
              </a>
            </div>
            <div>
              {recentSubmissions.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <div className="text-2xl mb-2">&#x2705;</div>
                  <div>No pending submissions</div>
                </div>
              ) : (
                recentSubmissions.map((item) => {
                  const timeAgo = formatTimeAgo(item.created_at);
                  return (
                    <div key={item.id} className="flex items-center p-3 sm:p-4 border-b border-gray-200 last:border-b-0">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-md bg-gray-200 flex items-center justify-center text-gray-500 text-lg sm:text-xl mr-3 flex-shrink-0">
                        &#x1F4CC;
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-slate-800 text-sm truncate">{item.name || 'Untitled'}</div>
                        <div className="text-xs text-gray-500 truncate">SKU: {item.sku || 'Unknown'} | {timeAgo}</div>
                      </div>
                      <a href="/submissions" className="px-2 sm:px-3 py-1.5 bg-blue-500 text-white rounded text-xs hover:bg-blue-600 no-underline">
                        Review
                      </a>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Recent Activity */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="bg-slate-700 text-white px-4 sm:px-5 py-3 sm:py-4 flex justify-between items-center">
              <span className="font-bold text-sm sm:text-base">&#x1F4C8; Recent Activity</span>
              <a href="/stats" className="text-gray-300 text-xs sm:text-sm hover:text-white no-underline">
                View All
              </a>
            </div>
            <div className="p-3 sm:p-4">
              {recentActivity.length === 0 ? (
                <div className="p-4 text-center text-gray-500">
                  <div className="text-xl mb-2">&#x1F4CA;</div>
                  <div className="text-sm">No recent activity</div>
                </div>
              ) : (
                recentActivity.map((item) => (
                  <div key={item.id} className="flex items-center py-2.5 sm:py-3 border-b border-gray-200 last:border-b-0">
                    <div className={`w-8 h-8 sm:w-9 sm:h-9 rounded-full ${item.iconBg} flex items-center justify-center text-sm mr-3 flex-shrink-0`} dangerouslySetInnerHTML={{ __html: item.icon }} />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-slate-800 text-sm truncate">{item.title}</div>
                      <div className="text-xs text-gray-500 truncate">{item.meta}</div>
                    </div>
                    <div className="text-xs text-gray-400 ml-2 flex-shrink-0">{formatTimeAgo(item.createdAt)}</div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Analytics Chart */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="bg-slate-700 text-white px-4 sm:px-5 py-3 sm:py-4 flex justify-between items-center">
              <span className="font-bold text-sm sm:text-base">&#x1F4CA; API Requests (7 days)</span>
              <a href="/stats" className="text-gray-300 text-xs sm:text-sm hover:text-white no-underline">
                Full Analytics
              </a>
            </div>
            <div className="p-4 sm:p-5">
              <div className="flex items-end gap-1 sm:gap-2 h-24 sm:h-36">
                {chartData.percentages.map((height, i) => (
                  <div
                    key={i}
                    className="flex-1 bg-gradient-to-t from-blue-500 to-blue-400 rounded-t transition-all duration-500"
                    style={{ height: `${Math.max(height, 2)}%` }}
                  />
                ))}
              </div>
              <div className="flex gap-1 sm:gap-2 mt-2">
                {chartData.days.map((day, i) => (
                  <span key={i} className="flex-1 text-center text-xs text-gray-500">{day}</span>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - 1/3 width on desktop */}
        <div className="flex flex-col gap-4 sm:gap-6">
          {/* Quick Actions */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="bg-slate-700 text-white px-4 sm:px-5 py-3 sm:py-4">
              <span className="font-bold text-sm sm:text-base">&#x26A1; Quick Actions</span>
            </div>
            <div className="p-4 sm:p-5 flex flex-col gap-2">
              {[
                { icon: '&#x2705;', iconBg: 'bg-yellow-100', label: 'Moderate Submissions', badge: stats?.pendingSubmissions ? `${stats.pendingSubmissions} pending` : null, href: '/submissions' },
                { icon: '&#x1F4CC;', iconBg: 'bg-blue-100', label: 'Manage Pins', badge: stats?.pendingPins ? `${stats.pendingPins} pending` : null, href: '/pins' },
                { icon: '&#x1F4CA;', iconBg: 'bg-green-100', label: 'View Statistics', badge: null, href: '/stats' },
                { icon: '&#x2699;&#xFE0F;', iconBg: 'bg-gray-200', label: 'Global Settings', badge: null, href: '/settings' },
              ].map((action, i) => (
                <a
                  key={i}
                  href={action.href}
                  className="flex items-center gap-2.5 p-2.5 sm:p-3 border border-gray-200 rounded-md hover:bg-gray-50 hover:border-blue-400 transition-colors no-underline text-slate-800"
                >
                  <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-md ${action.iconBg} flex items-center justify-center text-sm`} dangerouslySetInnerHTML={{ __html: action.icon }} />
                  <span className="text-sm">{action.label}</span>
                  {action.badge && (
                    <span className="ml-auto text-xs px-2 py-0.5 rounded bg-yellow-100 text-yellow-800">
                      {action.badge}
                    </span>
                  )}
                </a>
              ))}
            </div>
          </div>

          {/* System Health */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="bg-slate-700 text-white px-4 sm:px-5 py-3 sm:py-4">
              <span className="font-bold text-sm sm:text-base">&#x1F3E5; System Health</span>
            </div>
            <div className="p-4 sm:p-5 grid grid-cols-2 gap-3 sm:gap-4">
              {healthItems.map((item, i) => (
                <div key={i} className="flex items-center gap-2">
                  <div className={`w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full flex-shrink-0 ${item.status === 'healthy' ? 'bg-green-500' : item.status === 'warning' ? 'bg-yellow-500' : 'bg-red-500'}`} />
                  <span className="text-xs sm:text-sm text-slate-800 truncate">{item.label}</span>
                  <span className="text-xs text-gray-500 ml-auto flex-shrink-0">{item.value}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Storage Stats */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="bg-slate-700 text-white px-4 sm:px-5 py-3 sm:py-4">
              <span className="font-bold text-sm sm:text-base">&#x1F4BE; Storage Usage</span>
            </div>
            <div className="p-4 sm:p-5 flex flex-col gap-3 sm:gap-4">
              {storageItems.map((item, i) => (
                <div key={i}>
                  <div className="flex justify-between mb-1">
                    <span className="text-xs sm:text-sm text-slate-800">{item.label}</span>
                    <span className="text-xs sm:text-sm text-gray-500">{item.used}</span>
                  </div>
                  <div className="h-1.5 sm:h-2 bg-gray-200 rounded overflow-hidden">
                    <div className={`h-full ${item.color} transition-all duration-500`} style={{ width: `${item.percent}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
